// 07 — Setup. Two tabs: the yard plan (spots, shade through the day) and the
// coop (roof, ventilation, what's inside).
//
// The plan is drawn from a spot list and moved by the hour. Everything the plan
// shows about shade, water temperature and the boss hen's sightlines is the
// mockup's own geometry, so the numbers move the way the mockup moves them; the
// spots themselves are PLAN-04's to replace.

import { t } from '../../i18n';
import { chip } from '../components/chip';
import { segmented } from '../components/segmented';
import { button, div, h, link, paint } from '../dom';
import { createPlanView, planSpotView, type PlanLayer, type PlanSpotView } from '../planView';
import {
  placeholderAirTemp,
  placeholderInsideSpots,
  placeholderPlanSpots,
  placeholderRoofs,
  placeholderVents,
  type PlanSpot,
} from '../placeholders';
import { forecastAirTempF, formatForecastTempF, leagueForecast } from '../../sim/forecast';
import type { Screen, ScreenContext } from '../router';

export function setupScreen(): Screen<HTMLElement> {
  return {
    id: 'setup',
    render: (ctx: ScreenContext<HTMLElement>) => {
      const { store, shell } = ctx.services;
      const state = store.getState();
      const spots = placeholderPlanSpots(state.housing);
      const inside = placeholderInsideSpots();
      // In a league the air on this screen is the season's, day by day
      // (REFOCUS §11), so the plan a keeper is judging is the one every other
      // player in the season is judging. Normal has no league and keeps the
      // mockup's own day.
      //
      // Read through `state` rather than captured, because the league day rolls
      // over while this screen is open: a plan drawn against yesterday's
      // league is the exact mistake the forecast exists to stop.
      const leagueDayAt = () => leagueForecast(state)?.days[0] ?? null;
      const airAt = (hour: number): number => {
        const day = leagueDayAt();
        return day ? forecastAirTempF(day, hour) : placeholderAirTemp(hour);
      };

      let tab: 'plan' | 'coop' = 'plan';
      let hour = 14;
      const layers: Record<PlanLayer, boolean> = { shade: true, boss: true, rodents: true };
      // Fixture choices are the player's, kept in the shell for this screen.
      // PLAN-04 owns where they live.
      let roof = 'tin';
      let vent = 'ridge';

      const plan = createPlanView(spots, airAt);
      const slider = h('input', {
        class: 'cf-hours__range',
        attrs: { type: 'range', min: '6', max: '18', step: '1', value: String(hour) },
        on: {
          input: (event) => {
            hour = Number((event.target as HTMLInputElement).value);
            // Only the shade and the rows move. The screen is not rebuilt, and
            // the SVG is not either — the plan updates its own attributes.
            plan.setHour(hour);
            paintHours();
            paintRows();
          },
        },
      });
      const hourLabel = h('label', { class: 'cf-hours__label', attrs: { for: 'cf-hour' } });
      slider.setAttribute('id', 'cf-hour');
      const tempLabel = h('span', { class: 'cf-hours__temp' });
      const hours = h(
        'div',
        { class: 'cf-hours' },
        hourLabel,
        slider,
        tempLabel,
      );
      // The league day's own range, under the slider. The label beside it is the
      // air at the hour being dragged; this is the day that air belongs to, and
      // the number a keeper is actually planning against. Filled in by
      // `paintHours` so it moves with the league day.
      // `cf-muted` alone: `cf-small` is used as a class all over the shell but
      // has no rule behind it, and this branch is not the place to give every
      // one of those elements a font it has never had.
      const leagueNote = h('p', { class: 'cf-muted' });
      leagueNote.style.display = 'none';

      const layerRow = h('div', { class: 'cf-row' });
      const LAYERS: Array<[PlanLayer, string]> = [
        ['shade', 'setup.layerShade'],
        ['boss', 'setup.layerBoss'],
        ['rodents', 'setup.layerRodents'],
      ];
      for (const [key, labelKey] of LAYERS) {
        const toggle = button(
          {
            class: 'cf-layertoggle',
            text: t(labelKey),
            attrs: { 'aria-pressed': String(layers[key]), 'data-layer': key },
            on: {
              click: () => {
                layers[key] = !layers[key];
                plan.setLayers(layers);
                for (const [layerKey] of LAYERS) {
                  toggle.setAttribute('aria-pressed', String(layers[layerKey]));
                }
              },
            },
          },
        );
        layerRow.appendChild(toggle);
      }

      const rows = div({ class: 'cf-stack cf-stack--tight' });
      const coopHost = div({ class: 'cf-stack' });
      const head = div(
        { class: 'cf-screen__head' },
        link({
          class: 'cf-btn cf-btn--link',
          text: t('coop.backToCoop'),
          on: { click: () => ctx.router.back() },
        }),
        segmented<'plan' | 'coop'>({
          label: t('setup.tabLabel'),
          value: tab,
          options: [
            { value: 'plan', label: t('setup.tabPlan') },
            { value: 'coop', label: t('setup.tabCoop') },
          ],
          onChange: (value) => {
            tab = value;
            paintAll();
          },
        }),
      );
      const body = div({ class: 'cf-stack' });

      const screen = div(
        { class: 'cf-screen' },
        head,
        h('h1', { class: 'cf-display', text: t('setup.title') }),
        h('p', {
          class: 'cf-sub',
          text: t('setup.subtitle', { housing: t(`home.${state.housing}`) }),
        }),
        body,
      );

      function paintHours(): void {
        hourLabel.textContent = formatHour(hour);
        tempLabel.textContent = formatForecastTempF(airAt(hour), state.settings.useCelsius);
        const day = leagueDayAt();
        leagueNote.style.display = day ? '' : 'none';
        if (day) {
          leagueNote.textContent =
            `${t('weather.leagueDay', { day: day.leagueDay + 1 })} · ` +
            `${formatForecastTempF(day.highF, state.settings.useCelsius)} / ` +
            formatForecastTempF(day.lowF, state.settings.useCelsius);
        }
        slider.setAttribute('value', String(hour));
        (slider as HTMLInputElement).value = String(hour);
      }

      function paintRows(): void {
        const views = plan.views();
        paint(
          rows,
          views
            .map((view) => `${view.spot.n}:${view.light}:${view.waterTemp ?? ''}:${view.noteKey}:${view.extraKey ?? ''}`)
            .join('|'),
          () => div({ class: 'cf-stack cf-stack--tight' }, ...views.map(spotRow)),
        );
      }

      function paintCoop(): void {
        const roofs = placeholderRoofs();
        const vents = placeholderVents();
        paint(coopHost, `${roof}|${vent}`, () =>
          div(
            { class: 'cf-stack' },
            fixtureCard(t('setup.roofTitle'), t('setup.roofHint'), roofs, roof, (next) => {
              roof = next;
              paintCoop();
            }),
            fixtureCard(t('setup.ventTitle'), t('setup.ventHint'), vents, vent, (next) => {
              vent = next;
              paintCoop();
            }),
            div(
              { class: 'cf-card cf-row' },
              h('span', { class: 'cf-label', text: t('setup.roostTitle') }),
              h('span', { class: 'cf-small cf-muted', text: t('setup.roostNote') }),
            ),
            h('p', { class: 'cf-section', text: t('setup.insideTitle') }),
            div(
              { class: 'cf-stack cf-stack--tight' },
              ...inside.map((spot) => spotRow(planSpotView(spot, hour, airAt(hour)))),
            ),
          ),
        );
      }

      function paintAll(): void {
        paint(body, `${tab}|${hour}|${roof}|${vent}`, () =>
          div(
            { class: 'cf-stack' },
            tab === 'plan'
              ? div({ class: 'cf-stack' }, plan.element, hours, leagueNote, layerRow, rows)
              : coopHost,
          ),
        );
        // Coming back to the plan is the other moment the league day can have
        // moved on underneath the screen, so the note is refreshed here too.
        paintHours();
        if (tab === 'coop') paintCoop();
      }

      paintHours();
      paintRows();
      paintCoop();
      paintAll();
      // Switching tabs must not lose the hour the player dragged to.
      plan.setHour(hour);
      void shell;
      screen.tabIndex = -1;
      return screen;
    },
  };
}

/** 6–18 on the slider, as a keeper would say it. */
function formatHour(hour: number): string {
  if (hour === 12) return t('setup.hour12');
  return hour < 12 ? t('setup.hourAm', { hour }) : t('setup.hourPm', { hour: hour - 12 });
}

function spotRow(view: PlanSpotView): HTMLElement {
  return div(
    { class: `cf-spotrow${view.tone === 'danger' ? ' cf-spotrow--danger' : ''}` },
    h('span', { class: 'cf-spotrow__n', text: String(view.spot.n) }),
    div(
      { class: 'cf-stack cf-stack--tight cf-grow' },
      h('span', { class: 'cf-label', text: `${t(view.spot.nameKey)} · ${t(view.spot.itemKey)}` }),
      h('span', {
        class: `cf-small ${view.tone === 'danger' ? 'cf-warn' : 'cf-muted'}`,
        text: [view.noteKey ? t(view.noteKey, view.noteParams) : '', view.extraKey ? t(view.extraKey) : '']
          .filter(Boolean)
          .join(' · '),
      }),
    ),
    chip({ text: view.light === 'shade' ? t('setup.lightShade') : t('setup.lightSun'), tone: view.light === 'shade' ? 'shade' : 'warn' }),
  );
}

function fixtureCard(
  title: string,
  hint: string,
  fixtures: Array<{ id: string; labelKey: string; noteKey: string; cost: number }>,
  selected: string,
  onPick: (id: string) => void,
): HTMLElement {
  const active = fixtures.find((fixture) => fixture.id === selected) ?? fixtures[0];
  return div(
    { class: 'cf-card cf-stack cf-stack--tight' },
    h(
      'div',
      { class: 'cf-row' },
      h('span', { class: 'cf-label', text: title }),
      h('span', { class: 'cf-small cf-warn', text: t(active.noteKey) }),
    ),
    h('p', { class: 'cf-small cf-muted', text: hint }),
    div(
      { class: 'cf-row-grid cf-grid-3' },
      ...fixtures.map((fixture) =>
        button(
          {
            class: 'cf-choice',
            text: t(fixture.labelKey),
            attrs: { 'aria-pressed': String(fixture.id === selected) },
            on: { click: () => onPick(fixture.id) },
          },
        ),
      ),
    ),
  );
}

export type { PlanSpot };
